/**
 * Separate scheduled-run status from Safety Scan outcome.
 * Never claim a Scan was created unless COMPLETED and a report/result id is present.
 * Never hard-code a Walkway / baseline-window sentence.
 */

import { buildScanVerifyPath } from '../acceptance/scanVerifyNavigation';
import { customerSafeScheduledRunNotice } from './workspaceScheduledScanStatus';

export const SCAN_OUTCOME_CREATED = 'Created';
export const SCAN_OUTCOME_NOT_CREATED = 'Not created';
export const SCAN_OUTCOME_BLOCKED = 'Blocked';
export const SCAN_OUTCOME_FAILED = 'Failed';
export const SCAN_OUTCOME_CANCELLED = 'Cancelled';
export const SCAN_OUTCOME_NOT_APPLICABLE = 'Not applicable';
export const SCAN_OUTCOME_PENDING = 'Pending';
export const SCAN_OUTCOME_UNPROVEN = 'Not proven';

export const SCAN_NOT_CREATED_GENERIC_REASON =
  'This measurement window was not eligible for a Safety Scan.';

export const RUN_COMPLETED_SCAN_NOT_CREATED_COPY =
  'Latest scheduled run completed successfully. No Safety Scan was created because this window was not eligible.';

export const NO_CURRENT_SCAN_LABEL = 'No current Scan';
export const NO_SCAN_CREATED_FROM_LATEST_RUN = 'No Scan created from latest run';
export const MANAGED_CASE_OWNER_REQUIRED =
  'Assign a Case owner before creating this Safety Case.';

export const BASELINE_WINDOW_INELIGIBLE_CODE = 'BASELINE_WINDOW_INELIGIBLE';

/** Today's compose-only COMPLETED run used as the local fixture. */
export const COMPOSE_ONLY_COMPLETED_RUN_FIXTURE_ID = '522caed6';

export type ScanOutcomeKind =
  | 'created'
  | 'not_created'
  | 'blocked'
  | 'failed'
  | 'cancelled'
  | 'not_applicable'
  | 'pending'
  | 'unproven';

export type ScheduledRunScanOutcome = {
  runStatus: string | null;
  scanOutcomeKind: ScanOutcomeKind;
  scanOutcomeLabel: string;
  scanOutcomeReason: string | null;
  scanCreated: boolean;
  createdScanId: string | null;
  emptyCopy: string | null;
};

type RunLike = {
  id?: string | null;
  status?: string | null;
  terminal_reason_code?: string | null;
  terminal_reason_message?: string | null;
  links?: {
    result_summary_id?: string | null;
    report_id?: string | null;
  } | null;
};

function normalizeStatus(value: unknown): string {
  return String(value || '').trim().toUpperCase();
}

function reportIdOf(run: RunLike | null | undefined): string {
  return String(run?.links?.result_summary_id || run?.links?.report_id || '').trim();
}

function buyerSafeReason(run: RunLike | null | undefined): string | null {
  return customerSafeScheduledRunNotice(run?.terminal_reason_message);
}

function completedNoScanReason(run: RunLike | null | undefined): string {
  const code = normalizeStatus(run?.terminal_reason_code);
  const fromBackend = buyerSafeReason(run);
  if (code === BASELINE_WINDOW_INELIGIBLE_CODE) {
    return fromBackend || SCAN_NOT_CREATED_GENERIC_REASON;
  }
  return fromBackend || SCAN_NOT_CREATED_GENERIC_REASON;
}

export function scheduledRunCreatedScanHref(args: {
  siteId?: string | null;
  reportId?: string | null;
}): string | null {
  const reportId = String(args.reportId || '').trim();
  if (!reportId) return null;
  return buildScanVerifyPath({
    reportId,
    siteId: args.siteId,
  });
}

export function resolveScheduledRunScanOutcome(
  run: RunLike | null | undefined,
): ScheduledRunScanOutcome {
  if (!run) {
    return {
      runStatus: null,
      scanOutcomeKind: 'not_applicable',
      scanOutcomeLabel: SCAN_OUTCOME_NOT_APPLICABLE,
      scanOutcomeReason: null,
      scanCreated: false,
      createdScanId: null,
      emptyCopy: null,
    };
  }

  const status = normalizeStatus(run.status);
  const createdId = reportIdOf(run);
  const created = status === 'COMPLETED' && Boolean(createdId);
  if (created) {
    return {
      runStatus: status,
      scanOutcomeKind: 'created',
      scanOutcomeLabel: SCAN_OUTCOME_CREATED,
      scanOutcomeReason: null,
      scanCreated: true,
      createdScanId: createdId,
      emptyCopy: null,
    };
  }

  const reason = buyerSafeReason(run);
  if (status === 'COMPLETED') {
    return {
      runStatus: status,
      scanOutcomeKind: 'not_created',
      scanOutcomeLabel: SCAN_OUTCOME_NOT_CREATED,
      scanOutcomeReason: completedNoScanReason(run),
      scanCreated: false,
      createdScanId: null,
      emptyCopy: RUN_COMPLETED_SCAN_NOT_CREATED_COPY,
    };
  }
  if (status === 'FAILED') {
    return {
      runStatus: status,
      scanOutcomeKind: 'failed',
      scanOutcomeLabel: SCAN_OUTCOME_FAILED,
      scanOutcomeReason: reason,
      scanCreated: false,
      createdScanId: null,
      emptyCopy: null,
    };
  }
  if (status === 'CANCELLED') {
    return {
      runStatus: status,
      scanOutcomeKind: 'cancelled',
      scanOutcomeLabel: SCAN_OUTCOME_CANCELLED,
      scanOutcomeReason: reason,
      scanCreated: false,
      createdScanId: null,
      emptyCopy: null,
    };
  }
  if (status === 'INSUFFICIENT_COVERAGE') {
    return {
      runStatus: status,
      scanOutcomeKind: 'blocked',
      scanOutcomeLabel: SCAN_OUTCOME_BLOCKED,
      scanOutcomeReason: reason || 'Insufficient evidence to create a Safety Scan.',
      scanCreated: false,
      createdScanId: null,
      emptyCopy: null,
    };
  }
  if (status === 'SKIPPED_MISSED' || status === 'SKIPPED_OVERLAP') {
    return {
      runStatus: status,
      scanOutcomeKind: 'not_applicable',
      scanOutcomeLabel: SCAN_OUTCOME_NOT_APPLICABLE,
      scanOutcomeReason: reason,
      scanCreated: false,
      createdScanId: null,
      emptyCopy: null,
    };
  }
  if (['EXPECTED', 'QUEUED', 'RUNNING', 'DEFERRED'].includes(status)) {
    return {
      runStatus: status,
      scanOutcomeKind: 'pending',
      scanOutcomeLabel: SCAN_OUTCOME_PENDING,
      scanOutcomeReason: null,
      scanCreated: false,
      createdScanId: null,
      emptyCopy: null,
    };
  }
  if (!status) {
    return {
      runStatus: null,
      scanOutcomeKind: 'not_applicable',
      scanOutcomeLabel: SCAN_OUTCOME_NOT_APPLICABLE,
      scanOutcomeReason: null,
      scanCreated: false,
      createdScanId: null,
      emptyCopy: null,
    };
  }
  return {
    runStatus: status,
    scanOutcomeKind: 'unproven',
    scanOutcomeLabel: SCAN_OUTCOME_UNPROVEN,
    scanOutcomeReason: reason,
    scanCreated: false,
    createdScanId: null,
    emptyCopy: null,
  };
}

export function composeOnlyCompletedRunFixture(overrides: Partial<RunLike> = {}): RunLike {
  return {
    id: COMPOSE_ONLY_COMPLETED_RUN_FIXTURE_ID,
    status: 'COMPLETED',
    terminal_reason_code: null,
    terminal_reason_message: null,
    links: { result_summary_id: null, report_id: null },
    ...overrides,
  };
}

export function compactCurrentScanEmptyReason(
  outcome: ScheduledRunScanOutcome,
  nextScheduled?: string | null,
): string | null {
  if (outcome.scanCreated) return null;
  const next = String(nextScheduled || '').trim();
  if (outcome.scanOutcomeKind === 'not_created') {
    return next ? `${NO_SCAN_CREATED_FROM_LATEST_RUN}. Next scheduled ${next}` : NO_SCAN_CREATED_FROM_LATEST_RUN;
  }
  if (outcome.scanOutcomeKind === 'pending' && next) {
    return `Next scheduled ${next}`;
  }
  return outcome.scanOutcomeReason;
}
